// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * What is on the machine right now (design doc §9.5, US-C-01).
 *
 * There is no column for a position's `origin`. Its four values are internal codes, and
 * three of them are already visible another way: a critical slot has a serial and the 关键件
 * chip, a counted BOM line a quantity, a free-form position appears only on a no-BOM
 * archive. The fourth — a position a change added to a BOM archive (BOM_EXTRA) — is the one
 * worth seeing, and it is marked 变更新增 beside the position name: the machine has drifted
 * from its baseline, which the design treats as normal, not as an error to hide (§8.1).
 */

import { useQuery } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "@/api/endpoints";
import type { ArchiveItem } from "@/api/types";
import { materialInfoOf } from "@/archive/partState";
import { foldNodes, inBomOrder } from "@/archive/tree";
import { moduleTypeLabel } from "@/components/BomModulesSummary";
import { FoldControls } from "@/components/FoldControls";
import { IconButton } from "@/components/IconButton";
import { ChevronIcon, WrenchIcon } from "@/components/icons";
import { PositionDialog, type PositionMode } from "@/components/PositionDialog";
import { ResizableTable } from "@/components/ResizableTable";
import { ScrappedTag } from "@/components/ScrappedTag";
import { usePermissions } from "@/hooks/usePermissions";
import { foldedToLevel, hiddenKeys, levelCount, parentKeys } from "@/tree/fold";
import { useArchive } from "./ArchiveDetailPage";

type PartFilter = "all" | "critical" | "counted";

const PART_FILTERS: PartFilter[] = ["all", "critical", "counted"];

export function ArchiveCurrent() {
  const { t, i18n } = useTranslation();
  const { archive } = useArchive();
  const { canRecord } = usePermissions();
  const moduleTypes = useQuery({ queryKey: ["module-types"], queryFn: api.moduleTypes.list });
  const typeLabel = (code: string) =>
    moduleTypeLabel(code, moduleTypes.data ?? [], i18n.language === "en-US");
  // Module slots whose module's contents are shown under them (§9.4); closed at first,
  // so the robot reads as its own parts and one line per module.
  const [openModules, setOpenModules] = useState<ReadonlySet<string>>(new Set());
  const toggleModule = (key: string) =>
    setOpenModules((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // Opens on the critical parts — the ones with a serial, which is what this list is read
  // for — unless there are none (a machine filed without a BOM may have only counted
  // parts), where that would open on an empty list. `null` until the operator picks.
  const [chosen, setShown] = useState<PartFilter | null>(null);
  const shown: PartFilter = chosen ?? (archive.items.some(isCritical) ? "critical" : "all");
  const [query, setQuery] = useState("");
  // By id, not the item itself: after a replacement made in the dialog the archive is
  // reloaded, and the dialog should show the position as it is now.
  const [openId, setOpenId] = useState<string | null>(null);
  const [openMode, setOpenMode] = useState<PositionMode | null>(null);
  const opened = archive.items.find((item) => item.position_id === openId);
  const active = archive.status === "ACTIVE";
  const openPosition = (item: ArchiveItem, mode: PositionMode | null = null) => {
    setOpenMode(mode);
    setOpenId(item.position_id);
  };

  const [folded, setFolded] = useState<ReadonlySet<string>>(new Set());
  const toggleFold = (positionId: string) =>
    setFolded((current) => {
      const next = new Set(current);
      if (next.has(positionId)) next.delete(positionId);
      else next.add(positionId);
      return next;
    });

  // In BOM order, as the tree the BOM describes, rather than by position code.
  const ordered = useMemo(() => inBomOrder(archive.items), [archive.items]);
  const nodes = useMemo(() => foldNodes(ordered), [ordered]);
  const parents = parentKeys(nodes);

  const needle = query.trim().toLowerCase();
  const filtered = ordered.filter(
    (item) => isShown(item, shown) && (!needle || matches(item, needle)),
  );
  // A remark names one unit ("肩", "关节2"); the same word in a part or material name
  // usually names a whole family — 59 lines of one machine have 关节 in their name. Rows
  // whose remark matched go first, so the one being looked for is not buried under those.
  const items = needle
    ? [
        ...filtered.filter((item) => remarkMatches(item, needle)),
        ...filtered.filter((item) => !remarkMatches(item, needle)),
      ]
    : filtered;
  const narrowed = shown !== "all" || needle !== "";
  // While filtering or searching every match is shown, wherever it sits in the tree.
  const hidden = narrowed ? new Set<string>() : hiddenKeys(nodes, folded);
  const rows = items.filter((item) => !hidden.has(item.position_id));

  return (
    <>
      {/* Same chips as the filing screen, with counts from the whole machine: a filter
          answers "show me less", never "there is less". The search runs over what is on
          the page — position, serial, material and remark — so finding "the motor at
          joint 2" or "where did SN-123 go" needs no round trip. */}
      <div className="filters">
        <input
          type="search"
          aria-label={t("common.search")}
          autoComplete="off"
          placeholder={t("archive.findPart")}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <div className="chip-group" role="group" aria-label={t("archive.showRows")}>
          {PART_FILTERS.map((option) => (
            <button
              key={option}
              type="button"
              className={shown === option ? "chip chip-on" : "chip"}
              aria-pressed={shown === option}
              onClick={() => setShown(option)}
            >
              {t(`archive.filter.${option}`, {
                n: archive.items.filter((item) => isShown(item, option)).length,
              })}
            </button>
          ))}
        </div>
        <FoldControls
          levels={levelCount(nodes)}
          disabled={narrowed}
          onLevel={(level) => setFolded(level === null ? new Set() : foldedToLevel(nodes, level))}
        />
        {narrowed && (
          <span className="muted">
            {t("bom.shownLines", { n: items.length, total: archive.items.length })}
          </span>
        )}
      </div>

      {items.length === 0 && narrowed ? (
        <p className="muted">{t("bom.noMatchingLines")}</p>
      ) : (
        <ResizableTable storageKey="archive-current-2">
          {/* Keyed anew whenever the columns move: saved widths are matched by position. */}
          <thead>
            <tr>
              {/* Where on the machine, first: the list is read part by part. */}
              <th className="col-part-name">{t("bom.partName")}</th>
              <th>{t("archive.position")}</th>
              <th>{t("bom.drawingNo")}</th>
              <th>{t("archive.serial")}</th>
              <th>{t("archive.quantity")}</th>
              <th>{t("bom.materialCode")}</th>
              <th>{t("bom.remark")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => (
              <Fragment key={`${item.position_id}-${item.slot_no ?? 0}`}>
                <tr
                  className={
                    [
                      item.off_bom && "is-off-bom",
                      // Nothing left in the position: the part was taken off, which is the one
                      // thing on this list that must not be read past.
                      item.quantity === 0 && "is-removed",
                      isCritical(item) && "row-clickable",
                    ]
                      .filter(Boolean)
                      .join(" ") || undefined
                  }
                  // A critical part opens its history. The whole row is the target for the
                  // pointer; the name is a button for the keyboard.
                  onClick={isCritical(item) ? () => openPosition(item) : undefined}
                >
                  {/* From the BOM line; a position added by a change has none. */}
                  <td>{item.part_name || "—"}</td>
                  <td>
                    {parents.has(item.position_id) && !narrowed ? (
                      <button
                        type="button"
                        className={
                          folded.has(item.position_id)
                            ? "row-toggle tooltip-trigger"
                            : "row-toggle row-toggle-open tooltip-trigger"
                        }
                        aria-expanded={!folded.has(item.position_id)}
                        aria-label={t(
                          folded.has(item.position_id) ? "tree.expand" : "tree.collapse",
                          {
                            name: item.position_name,
                          },
                        )}
                        data-tooltip={t(
                          folded.has(item.position_id) ? "tree.expand" : "tree.collapse",
                          { name: item.position_name },
                        )}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleFold(item.position_id);
                        }}
                      >
                        <ChevronIcon size={14} />
                      </button>
                    ) : (
                      <span className="row-toggle-spacer" />
                    )}
                    {isCritical(item) ? (
                      <button
                        type="button"
                        className="row-open"
                        aria-label={t("position.open", { position: materialInfoOf(item) })}
                        onClick={(event) => {
                          event.stopPropagation();
                          openPosition(item);
                        }}
                      >
                        {materialInfoOf(item)}
                      </button>
                    ) : (
                      materialInfoOf(item)
                    )}
                    {item.origin === "BOM_EXTRA" && (
                      <span className="badge" title={t("archive.addedByChangeHint")}>
                        {t("archive.addedByChange")}
                      </span>
                    )}
                    {item.module_type && (
                      <span className="module-badge">{typeLabel(item.module_type)}</span>
                    )}
                  </td>
                  <td className="mono">{item.drawing_no || "—"}</td>
                  <td className="serial">
                    {item.module ? (
                      <ModuleCell
                        item={item}
                        open={openModules.has(item.position_id)}
                        onToggle={() => toggleModule(item.position_id)}
                      />
                    ) : (
                      <>
                        {item.serial_raw ?? "—"}
                        <ScrappedTag item={item} />
                      </>
                    )}
                  </td>
                  <td className={item.quantity === 0 ? "cell-nowrap quantity-removed" : undefined}>
                    {item.quantity}
                    {item.quantity === 0 && (
                      <span className="badge badge-removed" title={t("archive.removedHint")}>
                        {t("archive.removed")}
                      </span>
                    )}
                  </td>
                  <td className="mono cell-nowrap">
                    {item.material_code ?? "—"}
                    {/* The mark goes on the material, because that is the thing that differs:
                      the position is where the BOM said, the part in it is not. */}
                    {item.off_bom && (
                      <span className="badge badge-off-bom">{t("archive.offBom")}</span>
                    )}
                  </td>
                  {/* After the material, as on the filing screen: the note says which one this
                    is ("左前"), so it is read next to what was fitted there. */}
                  <td>
                    <Highlighted text={item.remark ?? ""} needle={needle} />
                  </td>
                  {/* On every row, greyed where it does not apply (§10.5) — and kept from also
                    opening the row's history underneath. A counted part opens the counted
                    form: replace or remove some of its units. One taken off entirely has
                    no units left to change; putting it back is an addition in 记录变更. */}
                  <td className="row-actions" onClick={(event) => event.stopPropagation()}>
                    <IconButton
                      label={t(isCritical(item) ? "position.replace" : "position.change")}
                      icon={<WrenchIcon />}
                      disabled={!canRecord || !active || (!isCritical(item) && item.quantity === 0)}
                      disabledReason={
                        !canRecord
                          ? t("roles.readOnly")
                          : !active
                            ? t("position.scrapped")
                            : t("position.nothingFitted")
                      }
                      onClick={() => openPosition(item, "replace")}
                    />
                  </td>
                </tr>
                {openModules.has(item.position_id) && (
                  <ModuleChildRows
                    items={item.children}
                    module={item.module}
                    depth={1}
                    path={item.position_id}
                    openModules={openModules}
                    onToggle={toggleModule}
                    typeLabel={typeLabel}
                  />
                )}
              </Fragment>
            ))}
          </tbody>
        </ResizableTable>
      )}

      {opened && (
        <PositionDialog
          // Remounted per opening, so a dialog opened from another icon starts in its form.
          key={`${opened.position_id}-${openMode ?? "history"}`}
          archive={archive}
          item={opened}
          initialMode={openMode}
          onClose={() => setOpenId(null)}
        />
      )}
    </>
  );
}

function isCritical(item: ArchiveItem): boolean {
  return item.tracking_mode === "SERIALIZED";
}

/**
 * A critical part is one tracked by serial — what filing called a slot. On an archive that
 * is the tracking mode rather than the BOM flag: a serialized part added during a change,
 * or entered by hand on a no-BOM archive, is just as much a part someone will look up by
 * its serial.
 */
function isShown(item: ArchiveItem, shown: PartFilter): boolean {
  return shown === "all" || (shown === "critical") === isCritical(item);
}

function matches(item: ArchiveItem, needle: string): boolean {
  return [
    item.position_code,
    item.position_name,
    materialInfoOf(item),
    item.part_name,
    item.drawing_no,
    item.serial_raw,
    item.material_code,
    item.remark,
  ].some((field) => field?.toLowerCase().includes(needle));
}

function remarkMatches(item: ArchiveItem, needle: string): boolean {
  return Boolean(item.remark?.toLowerCase().includes(needle));
}

/** `text` with the first case-insensitive occurrence of `needle` marked. */
function Highlighted({ text, needle }: { text: string; needle: string }) {
  const at = needle ? text.toLowerCase().indexOf(needle) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark>{text.slice(at, at + needle.length)}</mark>
      {text.slice(at + needle.length)}
    </>
  );
}

/** A module slot's serial cell: the module's archive number, its SN and the version this
 *  archive pinned, with the button that shows what is inside it. */
function ModuleCell({
  item,
  open,
  onToggle,
}: {
  item: ArchiveItem;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const module = item.module;
  if (!module) return null;
  return (
    <span className="module-cell" onClick={(event) => event.stopPropagation()}>
      {/* Children not loaded (an archive too big to expand at once) open all the same. */}
      {(item.children == null || item.children.length > 0) && (
        <button
          type="button"
          className={open ? "row-toggle row-toggle-open" : "row-toggle"}
          aria-expanded={open}
          aria-label={t(open ? "moduleArchive.hideContents" : "moduleArchive.showContents", {
            archiveNo: module.archive_no,
          })}
          onClick={onToggle}
        >
          <ChevronIcon size={14} />
        </button>
      )}
      <Link to={`/archives/${module.archive_id}`}>{module.archive_no}</Link>{" "}
      <span className="mono">{module.module_sn}</span>{" "}
      <span className="badge badge-version">v{module.version_no}</span>
    </span>
  );
}

/** A module's contents under its slot, read only, indented by level; its own modules
 *  open the same way. Changes to them go through the module's own page (§9.6). */
function ModuleChildRows({
  items,
  module,
  depth,
  path,
  openModules,
  onToggle,
  typeLabel,
}: {
  /** Null when not loaded with the archive: then read from the module's pinned version. */
  items: ArchiveItem[] | null | undefined;
  module?: ArchiveItem["module"];
  depth: number;
  path: string;
  openModules: ReadonlySet<string>;
  onToggle: (key: string) => void;
  typeLabel: (code: string) => string;
}) {
  const pinned = useQuery({
    queryKey: ["archive-version", module?.archive_id, module?.version_no],
    queryFn: () => api.archives.version(module?.archive_id ?? "", module?.version_no ?? 0),
    enabled: items == null && Boolean(module),
  });
  const loaded = items ?? pinned.data?.items ?? [];
  return (
    <>
      {inBomOrder(loaded).map((child) => {
        const key = `${path}/${child.position_id}`;
        return (
          <Fragment key={key}>
            <tr className={child.off_bom ? "module-child is-off-bom" : "module-child"}>
              <td>{child.part_name || "—"}</td>
              <td style={{ paddingLeft: `${8 + depth * 20}px` }}>
                {materialInfoOf(child)}
                {child.module_type && (
                  <span className="module-badge">{typeLabel(child.module_type)}</span>
                )}
              </td>
              <td className="mono">{child.drawing_no || "—"}</td>
              <td className="serial">
                {child.module ? (
                  <ModuleCell
                    item={child}
                    open={openModules.has(key)}
                    onToggle={() => onToggle(key)}
                  />
                ) : (
                  <>
                    {child.serial_raw ?? "—"}
                    <ScrappedTag item={child} />
                  </>
                )}
              </td>
              <td>{child.quantity}</td>
              <td className="mono cell-nowrap">{child.material_code ?? "—"}</td>
              <td>{child.remark ?? ""}</td>
              <td />
            </tr>
            {openModules.has(key) && (
              <ModuleChildRows
                items={child.children}
                module={child.module}
                depth={depth + 1}
                path={key}
                openModules={openModules}
                onToggle={onToggle}
                typeLabel={typeLabel}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
}
